// Paths (Analysis > MeshCore > Paths): the payload shape served by
// GET /api/paths/meshcore and the pure logic the tab renders from it.
// This file has NO imports on purpose -- it is compiled on its own and
// covered by node:test (frontend/tests/paths.test.mjs), so every rule that
// decides what text or number the page shows lives here, not in JSX.
//
// Hops in the payload are numbers: 0 or more = an index into `nodes` (a
// repeater our resolver matched to exactly one node); below 0 = a hop whose
// short ID did not match one node, -(k+1) = index k of `unclear`.

export type PathsWindow = "24h" | "7d";
export const PATHS_WINDOWS: PathsWindow[] = ["24h", "7d"];

export const DASH = "—";

export interface PathsNode {
  id: string;
  name: string;
}

export interface PathsUnclear {
  hex: string;
  /** 0 = no known node has this short ID; otherwise how many do. */
  candidates: number;
}

export interface PathsObserver {
  name: string;
  n: number;
}

export interface PathsFullPath {
  /** The hops of the whole path, in order. */
  t: number[];
  n: number;
  /** Where the route starts inside `t`. */
  at: number;
  /** The packet's origin when it can be named (a signed advert), else null. */
  o: number | null;
  /** Positions in `t` that only the rest of the path could name ("Resolved By Path"); absent when none. */
  r?: number[];
}

export interface PathsRoute {
  t: number[];
  /** Packets that used the route in the window. */
  n: number;
  /** Packets per day, last 7 days, oldest first. */
  tr: number[];
  /** Packets per hour over the window, oldest first. */
  h: number[];
  /** Distance of each hop (between consecutive hops of the route), km; null when unknown. */
  km: (number | null)[];
  o: PathsObserver[];
  p: PathsFullPath[];
}

export interface PathsLoopAgg {
  loops: number;
  worst: number;
  packets: number;
  last_seen: string;
}

export interface PathsLoopExample {
  time: string;
  observer: string | null;
  one_byte: boolean;
  path: number[];
  /** Positions in `path` holding the repeated hop. */
  marks: number[];
}

export interface PathsLoopRow {
  node: number;
  areas: string[];
  /** Loops with IDs of 2 or 3 bytes; null when the repeater only has 1-byte ones. */
  wide: PathsLoopAgg | null;
  /** Every loop, 1-byte ones included. */
  all: PathsLoopAgg | null;
  one_byte_events: number;
  examples: PathsLoopExample[];
}

export type RouteLists = Record<string, { clear: Record<string, number[]>; unclear: Record<string, number[]> }>;

export interface PathsPayload {
  window: PathsWindow;
  window_start: string;
  window_end: string;
  hours: number;
  trend_start: string;
  trend_days: number;
  packets_total: number;
  areas: { code: string; name: string }[];
  routes: Record<string, PathsRoute>;
  lists: RouteLists;
  loops: { rows: PathsLoopRow[]; unattributed_events: number };
  nodes: PathsNode[];
  unclear: PathsUnclear[];
  generated_at?: string;
}

export type PathsResponse = PathsPayload | { pending: true };

export function isPending(r: PathsResponse | null | undefined): r is { pending: true } {
  return !!r && "pending" in r && (r as { pending?: boolean }).pending === true;
}

// --- groups ---------------------------------------------------------------------

export interface PathsGroup {
  key: string;
  label: string;
}

export const PATHS_GROUPS: PathsGroup[] = [
  { key: "2", label: "2 hops" },
  { key: "3", label: "3 hops" },
  { key: "4", label: "4 hops" },
  { key: "5", label: "5+ hops" },
];

export const ALL = "";

/** Route ids to list for a location ("" = all areas), the unclear checkbox and a length group. */
export function routeIds(p: PathsPayload, loc: string, includeUnclear: boolean, group: string): number[] {
  const per = p.lists[loc];
  if (!per) return [];
  return (includeUnclear ? per.unclear : per.clear)[group] ?? [];
}

/** The listed routes, in the stored order (busiest first), each with its route entry. */
export function routeRows(p: PathsPayload, loc: string, includeUnclear: boolean, group: string): { id: number; route: PathsRoute }[] {
  const out: { id: number; route: PathsRoute }[] = [];
  for (const id of routeIds(p, loc, includeUnclear, group)) {
    const route = p.routes[String(id)];
    if (route) out.push({ id, route });
  }
  return out;
}

// --- hops --------------------------------------------------------------------------

export interface HopView {
  /** The name shown: a repeater's name, or the short hex ID for an unclear hop. */
  label: string;
  /** Node key for the page link; null for an unclear hop. */
  nodeId: string | null;
  /** Explanation for an unclear hop; null for a named one. */
  tip: string | null;
}

export function unclearTip(candidates: number): string {
  return candidates > 0 ? `ambiguous: ${candidates} candidates` : "unknown";
}

export function hopView(p: Pick<PathsPayload, "nodes" | "unclear">, t: number): HopView {
  if (t >= 0) {
    const n = p.nodes[t];
    return { label: n ? n.name : DASH, nodeId: n ? n.id : null, tip: null };
  }
  const u = p.unclear[-t - 1];
  return { label: u ? u.hex : DASH, nodeId: null, tip: u ? unclearTip(u.candidates) : "unknown" };
}

/** Plain text of a route: names (or short IDs) joined with arrows. */
export function routeText(p: Pick<PathsPayload, "nodes" | "unclear">, tokens: number[]): string {
  return tokens.map((t) => hopView(p, t).label).join(" → ");
}

// --- numbers ------------------------------------------------------------------------

/** Share of the window's 2+-hop packets, one decimal; a dash when there are none. */
export function pctText(n: number, total: number): string {
  if (!total || total <= 0) return DASH;
  const v = Math.round((n / total) * 1000) / 10;
  return `${v.toFixed(1)}%`;
}

export function kmText(km: number | null | undefined): string {
  if (km === null || km === undefined || Number.isNaN(km)) return DASH;
  return `${(Math.round(km * 10) / 10).toFixed(1)} km`;
}

export function packetsText(n: number): string {
  return n.toLocaleString("en-US");
}

// --- local time --------------------------------------------------------------------------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : new Date(t);
}

function two(n: number): string {
  return String(n).padStart(2, "0");
}

/** "HH:00" in the viewer's time zone. */
export function localHour(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? `${two(d.getHours())}:00` : DASH;
}

/** "Thu 03:00" in the viewer's time zone. */
export function localWeekdayHour(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? `${WEEKDAYS[d.getDay()]} ${two(d.getHours())}:00` : DASH;
}

/** "Oct 3 12:00" in the viewer's time zone. */
export function localDayHour(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()} ${two(d.getHours())}:00` : DASH;
}

/** "Oct 3 12:45" in the viewer's time zone. */
export function localDayTime(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()} ${two(d.getHours())}:${two(d.getMinutes())}` : DASH;
}

function addHours(iso: string, hours: number): string | null {
  const d = parse(iso);
  return d ? new Date(d.getTime() + hours * 3600_000).toISOString() : null;
}

// --- trend (7 daily bars) ---------------------------------------------------------------

export interface TrendBar {
  n: number;
  /** 0..1 of the tallest day; 0 for an empty day. */
  height: number;
  /** "Oct 2 12:00 to Oct 3 12:00: 438 packets" */
  tip: string;
}

/** Seven daily buckets starting at `trendStart` (each 24 hours long, the newest ending at the window end). */
export function trendBars(trendStart: string, tr: number[]): TrendBar[] {
  const max = Math.max(0, ...tr);
  return tr.map((n, i) => {
    const from = addHours(trendStart, i * 24);
    const to = addHours(trendStart, (i + 1) * 24);
    return {
      n,
      height: max > 0 ? n / max : 0,
      tip: `${localDayHour(from)} to ${localDayHour(to)}: ${packetsText(n)} ${n === 1 ? "packet" : "packets"}`,
    };
  });
}

/** One sentence for the whole trend: each day starts at the time shown, oldest first. */
export function trendSummary(trendStart: string, tr: number[]): string {
  const parts = tr.map((n, i) => `${localDayHour(addHours(trendStart, i * 24))}: ${packetsText(n)}`);
  return `Packets per day, each day starting at the time shown: ${parts.join("; ")}`;
}

// --- hourly activity -----------------------------------------------------------------------

export interface HourBar {
  n: number;
  height: number;
  label: string;
  tip: string;
}

/** One bar per hour of the window, labelled in the viewer's local time (day included for 7 days). */
export function hourBars(windowStart: string, h: number[], windowKey: PathsWindow): HourBar[] {
  const max = Math.max(0, ...h);
  return h.map((n, i) => {
    const at = addHours(windowStart, i);
    const label = windowKey === "7d" ? localWeekdayHour(at) : localHour(at);
    return { n, height: max > 0 ? n / max : 0, label, tip: `${label}: ${packetsText(n)} ${n === 1 ? "packet" : "packets"}` };
  });
}

/** Which bars carry an axis label: every 3rd hour for 24h, one per day for 7d (counted back from the newest). */
export function axisLabelIndexes(n: number, windowKey: PathsWindow): number[] {
  const step = windowKey === "7d" ? 24 : 3;
  const out: number[] = [];
  for (let i = 0; i < n; i++) if ((n - 1 - i) % step === 0) out.push(i);
  return out;
}

// --- full paths -----------------------------------------------------------------------------

export interface PathPiece {
  token: number;
  /** True for the hops that make up the route itself. */
  inRoute: boolean;
  /** True when only the rest of the path named this repeater (shows the "Resolved By Path" tag). */
  byPath: boolean;
}

export function pathPieces(path: PathsFullPath, routeLength: number): PathPiece[] {
  const byPath = new Set(path.r ?? []);
  return path.t.map((token, i) => ({ token, inRoute: i >= path.at && i < path.at + routeLength, byPath: byPath.has(i) && token >= 0 }));
}

/** Short tag for a repeater that was named only through the path around it. */
export const PATH_TAG = "Resolved By Path";
export const PATH_TAG_TIP = "The short ID on its own matched several repeaters. The repeaters before and after it in the path, and distance, left only this one.";

/** The tag to show next to a hop, or null for a hop named by its ID alone. */
export function pathTag(byPath: boolean): string | null {
  return byPath ? PATH_TAG : null;
}

/** What the small badge reads; the full label is its hover. */
export const PATH_BADGE = "Path";

// --- loops -----------------------------------------------------------------------------------

export const SHARED_ID_NOTE = "may be two nodes sharing an ID";

export interface LoopView {
  row: PathsLoopRow;
  agg: PathsLoopAgg;
  /** Shown with the "may be two nodes sharing an ID" mark. */
  sharedIdMark: boolean;
}

/**
 * One row per repeater for a location and the 1-byte checkbox. Without the
 * checkbox only loops with 2- or 3-byte IDs count (a repeater with only 1-byte
 * loops is left out); with it, every loop counts and a repeater with any 1-byte
 * loop is marked. Most loops first.
 */
export function loopRows(p: PathsPayload, loc: string, showOneByte: boolean): LoopView[] {
  const out: LoopView[] = [];
  for (const row of p.loops.rows) {
    if (loc !== ALL && !row.areas.includes(loc)) continue;
    const agg = showOneByte ? row.all : row.wide;
    if (!agg) continue;
    out.push({ row, agg, sharedIdMark: showOneByte && row.one_byte_events > 0 });
  }
  return out.sort((a, b) => b.agg.loops - a.agg.loops || b.agg.packets - a.agg.packets || hopName(p, a.row.node).localeCompare(hopName(p, b.row.node)));
}

function hopName(p: Pick<PathsPayload, "nodes" | "unclear">, t: number): string {
  return hopView(p, t).label;
}

/** The looping paths to draw for a repeater: 1-byte ones only when the checkbox is on. Newest first. */
export function loopExamples(row: PathsLoopRow, showOneByte: boolean): PathsLoopExample[] {
  return row.examples.filter((e) => showOneByte || !e.one_byte).sort((a, b) => (a.time < b.time ? 1 : a.time > b.time ? -1 : 0));
}

/** "2× before": how many times the repeater's ID was already in the path when it passed the packet on. */
export function worstText(worst: number): string {
  return `${worst}× before`;
}

// --- text ---------------------------------------------------------------------------------------

export const PACKETS_TIP = "Each transmission counts once, on the longest path any observer heard for it.";
export const PCT_TIP = "Packets using this route, as a share of all packets in the window that had a path of 2 or more hops.";
export const TREND_TIP = "Packets per day over the last 7 days, oldest on the left.";
export const UNCLEAR_TIP =
  "Also list routes with a hop that matches no known repeater or more than one. Those hops show their short ID.";
export const LOCATION_TIP = "Show routes with at least one repeater located in this area.";
export const ONE_BYTE_TIP = "A 1-byte ID can belong to several repeaters, so these loops may be two nodes sharing an ID.";
export const LOOPS_TIP = "How many times a looping path was heard passing through this repeater.";
export const WORST_TIP = "The most times this repeater's ID was already in the path when it passed the packet along.";
export const LOOP_PACKETS_TIP = "Different packets that looped through this repeater.";
export const OBSERVER_TIP = "Observers that heard this route's packets, and how many of them each one heard on this route.";
export const KM_TIP = "Distance between the two repeaters, from their reported positions.";

export const NO_ROUTES_TEXT = "No routes of this length in this window.";

export const NO_LOOPS_TEXT = "No loops in this window.";
